/** Full-screen preview of the PDF pages, with the button that downloads them. */

import { createPortal } from "react-dom";
import type { ExportData } from "@/components/tabs/home/pdfExport";
import { UI_SCALE } from '@/utils/uiScale';

interface ExportPreviewModalProps {
  data: ExportData;
  sessionId: string;
  downloading: boolean;
  onDownload: () => void;
  onClose: () => void;
}

export function ExportPreviewModal({ data, sessionId, downloading, onDownload, onClose }: ExportPreviewModalProps) {
  // A portal, so it sits above all split-screen layers.
  return createPortal(
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 4600,
        display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#f1f5f9', width: '100%', maxWidth: 900,
          display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* Modal header */}
        <div style={{ background: 'white', padding: '12px 16px', display: 'flex', zoom: UI_SCALE,
          alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          borderBottom: '1px solid #e2e8f0', gap: 12 }}
        >
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            📄 PDF Preview
            {sessionId && (
              /* Same line the exported pages carry, so what's on screen matches the file. */
              <span style={{ fontWeight: 600, fontSize: 12, color: '#64748b', fontFamily: 'monospace',
                background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 5, padding: '2px 6px',
                overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {sessionId}
              </span>
            )}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={onDownload}
              disabled={downloading}
              style={{ padding: '7px 16px', background: downloading ? '#94a3b8' : '#667eea',
                color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: downloading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
            >
              {downloading ? 'Generating…' : '⬇ Download PDF'}
            </button>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer',
                color: '#888', lineHeight: 1, padding: '0 4px' }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Scrollable page previews */}
        <div style={{ overflowY: 'auto', flex: 1, padding: 16,
          display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b',
              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Page 1 — Counter Timetable
            </div>
            <div style={{ background: 'white', borderRadius: 6, padding: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <img src={data.counterImg} style={{ width: '100%', display: 'block' }} alt="Counter timetable" />
            </div>
          </div>

          {data.stats && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b',
                textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                Page 2 — Manning Stats
              </div>
              <div style={{ background: 'white', borderRadius: 6, padding: 16,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' }}>
                <pre style={{ margin: 0, fontSize: 10, fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap', color: '#374151', lineHeight: 1.5 }}>
                  {data.stats}
                </pre>
              </div>
            </div>
          )}

          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b',
              textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              {data.stats ? 'Page 3' : 'Page 2'} — Officer Schedule
            </div>
            <div style={{ background: 'white', borderRadius: 6, padding: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <img src={data.officerImg} style={{ width: '100%', display: 'block' }} alt="Officer schedule" />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
